import { useState } from "react";
import {
  Package,
  User,
  HardDrive,
  Server,
  Check,
  CheckCircle,
  Plus,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface BoBCardProps {
  bob: {
    id: string;
    name: string;
    version: string;
    description: string;
    category: string;
    author: string;
    size_compressed: number;
    metadata?: {
      requires_connector?: string;
      requires_odoo?: string;
      changelog_url?: string;
      dependencies?: string[];
    };
  };
  isSelected: boolean;
  isInstalled?: boolean;
  onToggleSelect: () => void;
  onViewDetails: () => void;
}

// Category icon mapping
const categoryIcons: Record<string, typeof Package> = {
  finance: Package,
  hr: User,
  sales: Package,
  inventory: HardDrive,
  productivity: Server,
};

// Category color mapping
const categoryColors: Record<string, string> = {
  finance: "from-green-500/20 to-emerald-500/20",
  hr: "from-blue-500/20 to-sky-500/20",
  sales: "from-purple-500/20 to-violet-500/20",
  inventory: "from-orange-500/20 to-amber-500/20",
  productivity: "from-teal-500/20 to-cyan-500/20",
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export function BoBCard({
  bob,
  isSelected,
  isInstalled = false,
  onToggleSelect,
  onViewDetails,
}: BoBCardProps) {
  const CategoryIcon = categoryIcons[bob.category] || Package;
  const categoryColor = categoryColors[bob.category] || "from-slate-500/20 to-slate-600/20";

  return (
    <Card
      className={cn(
        "relative transition-all hover:shadow-lg",
        isSelected && "ring-2 ring-blue-500 bg-blue-500/5",
        isInstalled && "opacity-60 bg-slate-800/50"
      )}
      role="listitem"
    >
      {/* Selection checkbox */}
      <div className="absolute top-4 right-4 z-10">
        <label className={cn("cursor-pointer", isInstalled && "cursor-not-allowed")}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            disabled={isInstalled}
            className="sr-only"
            aria-label={`Select ${bob.name}`}
            aria-disabled={isInstalled}
          />
          <div
            className={cn(
              "h-6 w-6 rounded border-2 flex items-center justify-center transition-colors",
              isSelected
                ? "bg-blue-500 border-blue-500"
                : "border-slate-500 hover:border-blue-400",
              isInstalled && "opacity-40 cursor-not-allowed border-slate-600"
            )}
            aria-hidden="true"
          >
            {isSelected && <Check className="h-4 w-4 text-white" />}
          </div>
        </label>
      </div>

      <CardHeader>
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "h-12 w-12 rounded-lg bg-gradient-to-br flex items-center justify-center flex-shrink-0",
              categoryColor
            )}
            aria-hidden="true"
          >
            <CategoryIcon className="h-6 w-6 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0 pr-8">
            <CardTitle className="text-lg truncate text-slate-100">
              {bob.name}
            </CardTitle>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs">
                v{bob.version}
              </Badge>
              <Badge variant="secondary" className="text-xs capitalize">
                {bob.category}
              </Badge>
              {isInstalled && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Installed
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-slate-300 line-clamp-3">
          {bob.description}
        </p>

        <div className="space-y-2 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <User className="h-3 w-3" aria-hidden="true" />
            <span>By {bob.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <HardDrive className="h-3 w-3" aria-hidden="true" />
            <span>{formatBytes(bob.size_compressed)}</span>
          </div>
          {bob.metadata?.requires_odoo && (
            <div className="flex items-center gap-2">
              <Server className="h-3 w-3" aria-hidden="true" />
              <span>Requires Odoo {bob.metadata.requires_odoo}</span>
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onViewDetails}
          aria-label={`View details for ${bob.name}`}
        >
          <Info className="h-4 w-4 mr-2" aria-hidden="true" />
          Details
        </Button>
        <Button
          size="sm"
          className={cn("flex-1", isInstalled && "opacity-50 cursor-not-allowed")}
          onClick={onToggleSelect}
          disabled={isInstalled}
          variant={isInstalled ? "secondary" : isSelected ? "secondary" : "default"}
          aria-label={
            isInstalled
              ? `${bob.name} is already installed`
              : isSelected
              ? `Deselect ${bob.name}`
              : `Select ${bob.name}`
          }
        >
          {isInstalled ? (
            <>
              <CheckCircle className="h-4 w-4 mr-2" aria-hidden="true" />
              Installed
            </>
          ) : isSelected ? (
            <>
              <CheckCircle className="h-4 w-4 mr-2" aria-hidden="true" />
              Selected
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
              Select
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

// Skeleton loader for loading states
export function BoBCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-lg bg-slate-700/50 animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-slate-700/50 rounded animate-pulse w-3/4" />
            <div className="h-4 bg-slate-700/50 rounded animate-pulse w-1/2" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="h-4 bg-slate-700/50 rounded animate-pulse" />
          <div className="h-4 bg-slate-700/50 rounded animate-pulse w-5/6" />
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        <div className="h-9 bg-slate-700/50 rounded animate-pulse flex-1" />
        <div className="h-9 bg-slate-700/50 rounded animate-pulse flex-1" />
      </CardFooter>
    </Card>
  );
}
